import type { Ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import { isEmpty } from 'lodash-es';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import type { CommissionCountriesTab } from '@modules/affiliate-program-commissions/submodules/edit/components/CommissionCountriesTabs/types';
import type { IApiFormErrors } from '@modules/core/services/query/types';
import type { FormFlattenKey, FormFlattenValue, FormGroup, FormSchema } from '@modules/forms/components/VForm/types';
import type { IFormPlainDataListboxItem, IFormSelectInputOption } from '@modules/forms/types';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import { EAasmState, EExpectedAction } from '@modules/core/enums';
import sortByProperty from '@modules/core/utils/sortByProperty';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import { assert, isBoolean, isDate, isOptional, isString } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

import type {
  ITrackerGroupFilterItem,
  ITrackerGroupFilterItemErrors,
  ITrackerGroupsFilterItemData,
  ITrackerGroupsForm,
  ITrackerGroupsPayload,
} from '../../../types';
import {
  AFFILIATES_TRACKER_GROUPS_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATES_TRACKERS_GROUPS,
} from '../../../constants';
import { useSaveTrackerGroupsMutation } from '../../../mutations';
import {
  useShowAffiliateTrackersListQuery,
  useShowTrackerGroupItemQuery,
} from '../../../queries';

export interface ITrackerGroupsFormComposable {
  activeFilterIndex: Ref<number>;
  editFromPermission: Ref<boolean>;
  isSaveLoading: Ref<boolean>;
  errors: Ref<IApiFormErrors | undefined>;
  clearError(key: string): void;
  activeTrackerGroupFilter: Ref<ITrackerGroupFilterItem | undefined>;
  formSchema: Ref<FormSchema<ITrackerGroupsForm>>;
  formGroups: Ref<FormGroup<ITrackerGroupsForm>[]>;
  trackerGroupsTabs: Ref<CommissionCountriesTab[]>;
  displayFilterErrors: Ref<string[] | undefined>;
  trackersOptions: Ref<IFormPlainDataListboxItem<number>[]>;
  warnMessage: Ref<string | undefined>;
  onFormChange<T extends ITrackerGroupsForm, K extends FormFlattenKey<T>>(key: K, value: FormFlattenValue<T, K> | undefined): void;
  onSubmit(values: ITrackerGroupsForm): Promise<void>;
  onFilterAdd(): void;
  onFilterSelect(index: number): void;
  onFilterExpandToggle(index: number): void;
  onFilterRemove(index: number): void;
  onChangeFilterName(value?: string): void;
  onChangeValue(value?: number[]): void;
}

export default function useTrackerGroupsForm(): ITrackerGroupsFormComposable {
  const route = useRoute();
  const { hasPermission } = usePermissionsStore();
  const { getTranslation } = useI18n();
  const { t } = useTranslation();
  const { formatMonthDate } = useFormatMonthDate();

  const defaultGroupName = 'default';
  const activeFilterIndex = ref(0);
  const trackerGroupsFilters = ref<ITrackerGroupFilterItem[]>([]);
  const maxFilterId = ref(0);
  const dateValue = ref<Date | undefined>(new Date());

  const affiliateId = computed<number>(() => (isString(route.params.id) ? Number(route.params.id) : 0));
  const selectedMonth = computed<string | undefined>(() => dateValue.value ? formatMonthDate(dateValue.value) : undefined);

  const { data: trackersList, isLoading: isTrackersListLoading } = useShowAffiliateTrackersListQuery(affiliateId);
  const { data: trackerGroupItem, isLoading: isTrackerGroupItemLoading } = useShowTrackerGroupItemQuery(affiliateId, selectedMonth);

  const { mutateAsync: saveTrackerGroups, isLoading: isSaveLoading, errors, clearError } = useSaveTrackerGroupsMutation();

  const isEdit = computed(() => trackerGroupItem.value?.expected_action === EExpectedAction.UPDATE);

  const warnMessage = computed<string | undefined>(() => {
    const fromDate = trackerGroupItem.value?.message?.data?.from_date;
    const toDate = trackerGroupItem.value?.message?.data?.to_date;

    return isEmpty(trackerGroupItem.value?.message)
      ? undefined
      : t(
          trackerGroupItem.value.message.text,
          {
            replace: {
              ...trackerGroupItem.value.message.data,
              from_date: isString(fromDate) ? formatMonthDate(new Date(fromDate)) : fromDate,
              to_date: isString(toDate) ? formatMonthDate(new Date(toDate)) : toDate,
            },
          },
        );
  });

  const editFromPermission = computed(() => isEdit.value
    ? hasPermission(AFFILIATES_TRACKER_GROUPS_PERMISSIONS.EDIT).value
    : hasPermission(AFFILIATES_TRACKER_GROUPS_PERMISSIONS.CREATE).value,
  );

  const enableUseOfAnid2Options = computed<IFormSelectInputOption<boolean>[]>(() => [
    {
      value: true,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_GROUPS, 'FORM_ENABLE_USE_ANID_YES').value,
    },
    {
      value: false,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_GROUPS, 'FORM_ENABLE_USE_ANID_NO').value,
    },
  ]);

  const trackerGroupsFiltersWithErrors = computed<ITrackerGroupFilterItem[]>(() => (
    trackerGroupsFilters.value.map((item) => convertFilterItemToItemWithErrors(item))
  ));

  const activeTrackerGroupFilter = computed<ITrackerGroupFilterItem | undefined>(() => (
    trackerGroupsFiltersWithErrors.value[activeFilterIndex.value]
  ));

  const invalidTrackerGroupsFilters = computed<ITrackerGroupFilterItem[] | undefined>(() => (
    trackerGroupsFiltersWithErrors.value.filter((item) => !!item.errors)
  ));

  const formSchema = computed<FormSchema<ITrackerGroupsForm>>(() => ({
    date: {
      type: EFormInputTypes.DATE,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_GROUPS, 'FORM_DATE').value,
      value: dateValue.value,
      required: true,
      view: 'month',
      dateFormat: 'mm.yy',
    },
    trackerGroupsIndex: {
      type: EFormInputTypes.SLOT,
    },
    trackerListValues: {
      type: EFormInputTypes.SLOT,
    },
    enable_use_of_anid2: {
      type: EFormInputTypes.SELECT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_GROUPS, 'FORM_ENABLE_USE_ANID_LABEL').value,
      options: enableUseOfAnid2Options.value,
      required: true,
      value: activeTrackerGroupFilter.value?.enable_use_of_anid2,
      readonly: !editFromPermission.value,
    },
  }));

  const formGroups = computed<FormGroup<ITrackerGroupsForm>[]>(() => {
    const res: FormGroup<ITrackerGroupsForm>[] = [{
      items: ['date'],
    }];

    if (dateValue.value) {
      res.push({
        items: ['trackerGroupsIndex', 'trackerListValues', 'enable_use_of_anid2'],
        isLoading: isTrackerGroupItemLoading.value || isTrackersListLoading.value,
        minHeight: '120px',
      });
    }

    return res;
  });

  const trackerGroupsTabs = computed<CommissionCountriesTab[]>(() => {
    const tabs: CommissionCountriesTab[] = [];

    trackerGroupsFiltersWithErrors.value.forEach((item, index) => {
      if (!item._destroy) {
        tabs.push({
          index,
          label: item.label,
          emptyLabel: item.emptyLabel,
          expanded: item.expanded,
          isActive: index === activeFilterIndex.value,
        });
      }
    });

    return tabs;
  });

  const trackersOptions = computed<IFormPlainDataListboxItem<number>[]>(() => {
    if (!trackersList.value) {
      return [];
    }

    const selectedTrackers = trackerGroupsFilters.value.filter((item, index) => activeFilterIndex.value !== index && !item._destroy).flatMap((item) => item.trackers);

    return trackersList.value.trackers.filter((item) => !selectedTrackers.includes(item.id)).map((item) => ({
      id: item.id,
      value: item.value,
      check: activeTrackerGroupFilter.value?.trackers.includes(item.id),
    }));
  });

  const displayFilterErrors = computed<string[] | undefined>(() => {
    const errorNames = trackerGroupsFiltersWithErrors.value
      .filter((item, index) => !!item.errors && index !== activeFilterIndex.value)
      .map((item) => item.label || item.emptyLabel);

    return errorNames.length > 0
      ? [`${getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_GROUPS, 'VALIDATIONS_INVALID_SOME_GROUP').value}: ${errorNames.join(', ')}`]
      : undefined;
  });

  function onFormChange<
    T extends ITrackerGroupsForm,
    K extends FormFlattenKey<T>,
  >(
    key: K,
    value: FormFlattenValue<T, K> | undefined,
  ): void {
    if (key === 'date') {
      assert(isOptional(isDate, value));
      dateValue.value = value;
    }
    if (key === 'enable_use_of_anid2') {
      assert(isBoolean(value));
      onChangeEnableUseOfAnid2(value);
    }
  }

  async function onSubmit(values: ITrackerGroupsForm): Promise<void> {
    if (invalidTrackerGroupsFilters.value?.length || !editFromPermission.value) {
      return;
    }

    const payload: ITrackerGroupsPayload = {
      isEdit: isEdit.value,
      history_of_tracker_groups_change: {
        id: trackerGroupItem.value?.history_of_tracker_groups_change.id,
        date: formatMonthDate(values.date),
        affiliate_id: affiliateId.value,
        tracker_groups_attributes: trackerGroupsFiltersWithErrors.value.map(({
          isAllTrackers,
          label,
          emptyLabel,
          emptyLabelId,
          expanded,
          errors,
          ...item
        }) => ({
          ...item,
          name: isAllTrackers ? defaultGroupName : label || '',
          aasm_state: isAllTrackers ? EAasmState.DEFAULT : EAasmState.CUSTOM,
        })),
      },
    };

    try {
      await saveTrackerGroups(payload);
    } catch {}
  }

  function onFilterAdd(): void {
    trackerGroupsFilters.value.push(createTrackerGroupItem(false));
    activeFilterIndex.value = trackerGroupsFilters.value.length - 1;
    maxFilterId.value += 1;
  }

  function onFilterSelect(index: number): void {
    activeFilterIndex.value = index;
  }

  function onFilterExpandToggle(index: number): void {
    if (trackerGroupsFilters.value[index]) {
      trackerGroupsFilters.value.splice(index, 1, {
        ...trackerGroupsFilters.value[index],
        expanded: !trackerGroupsFilters.value[index].expanded,
      });
    }
  }

  function onFilterRemove(index: number): void {
    const revertActive = activeFilterIndex.value === index;

    const activeFilter = trackerGroupsFilters.value[index];

    if (activeFilter) {
      let activeFilterDiff = 0;

      if (activeFilter.id) {
        trackerGroupsFilters.value.splice(index, 1, {
          ...activeFilter,
          _destroy: 1,
        });
      } else {
        trackerGroupsFilters.value.splice(index, 1);
        activeFilterDiff = 1;
      }

      if (revertActive) {
        activeFilterIndex.value = 0;
      } else if (activeFilterIndex.value > index) {
        activeFilterIndex.value -= activeFilterDiff;
      }
    }
  }

  function onChangeFilterName(value?: string): void {
    const activeFilter = trackerGroupsFilters.value[activeFilterIndex.value];
    if (activeFilter) {
      trackerGroupsFilters.value[activeFilterIndex.value] = {
        ...activeFilter,
        label: value,
      };
    }
  }

  function onChangeValue(value?: number[]): void {
    const activeFilter = trackerGroupsFilters.value[activeFilterIndex.value];
    if (activeFilter) {
      trackerGroupsFilters.value[activeFilterIndex.value] = {
        ...activeFilter,
        trackers: value ?? [],
      };
    }
  }

  function onChangeEnableUseOfAnid2(value: boolean): void {
    const activeFilter = trackerGroupsFilters.value[activeFilterIndex.value];
    if (activeFilter) {
      trackerGroupsFilters.value[activeFilterIndex.value] = {
        ...activeFilter,
        enable_use_of_anid2: value,
      };
    }
  }

  function getFilterItemErrors(item: ITrackerGroupFilterItem): ITrackerGroupFilterItemErrors {
    const errors: ITrackerGroupFilterItemErrors = {};

    if (!item.isAllTrackers && item.trackers.length === 0) {
      errors.validSelection = [getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_GROUPS, 'VALIDATIONS_TRACKER_REQUIRED').value];
    }

    if (!item.label) {
      errors.validSelection = [
        ...errors.validSelection ?? [],
        getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_GROUPS, 'VALIDATION_GROUP_NAME_REQUIRED').value,
      ];
    }

    return errors;
  }

  function convertFilterItemToItemWithErrors(item: ITrackerGroupFilterItem): ITrackerGroupFilterItem {
    const errors: ITrackerGroupFilterItemErrors = item._destroy ? {} : getFilterItemErrors(item);

    return {
      ...item,
      emptyLabel: item.isAllTrackers
        ? ''
        : getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_GROUPS, 'FORM_GROUPS_NO_GROUP_NAME', {
          id: item.emptyLabelId,
        }).value,
      errors: Object.keys(errors).length > 0 ? errors : undefined,
    };
  }

  function recalculateFilters(): void {
    activeFilterIndex.value = 0;

    if (trackerGroupItem.value?.history_of_tracker_groups_change?.tracker_groups?.length) {
      trackerGroupsFilters.value = sortByProperty(
        trackerGroupItem.value?.history_of_tracker_groups_change?.tracker_groups.map((item) => {
          const isAllTrackers = item.aasm_state === EAasmState.DEFAULT;
          maxFilterId.value = item.id && item.id > maxFilterId.value ? item.id : maxFilterId.value;

          return createTrackerGroupItem(isAllTrackers, item);
        }),
        'isAllTrackers',
      );
    } else {
      trackerGroupsFilters.value = [createTrackerGroupItem(true)];
    }
  }

  function createTrackerGroupItem(isAllTrackers: boolean, item?: ITrackerGroupsFilterItemData): ITrackerGroupFilterItem {
    return {
      ...item,
      label: isAllTrackers ? defaultGroupName : item?.name,
      emptyLabel: '',
      emptyLabelId: maxFilterId.value + 1,
      trackers: item?.trackers ?? [],
      expanded: !isAllTrackers,
      enable_use_of_anid2: item?.enable_use_of_anid2 ?? false,
      isAllTrackers,
    };
  }

  watchImmediate(trackerGroupItem, recalculateFilters, { deep: true });

  return {
    activeFilterIndex,
    isSaveLoading,
    errors,
    clearError,
    editFromPermission,
    activeTrackerGroupFilter,
    formSchema,
    formGroups,
    trackerGroupsTabs,
    displayFilterErrors,
    trackersOptions,
    warnMessage,
    onFormChange,
    onSubmit,
    onFilterAdd,
    onFilterSelect,
    onFilterExpandToggle,
    onFilterRemove,
    onChangeFilterName,
    onChangeValue,
  };
}
